import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useLocation, useNavigate, useParams } from "react-router-dom";
import { getMovimenti, getFiltriMovimenti, getAnniDisponibili, setMovimentoMarcatura, runMarcatoreAuto } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import { openPrintWindow } from "@/lib/printWindow";
import { useAuth } from "@/lib/AuthContext";

const dmyToIso = (dmy) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}` : "";
};
const isoToDmy = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};
const parseDmy = (dmy) => {
  if (!dmy) return null;
  const [d, m, y] = dmy.split("/");
  if (!d || !m || !y) return null;
  return new Date(Number(y), Number(m) - 1, Number(d));
};
// Addebito non pagato e scaduto: importo > 0, nessuna data pagamento, scadenza passata.
const isScadutoNonPagato = (m) => {
  if (m.DtaPag) return false;
  if (!(Number(m.Imp) > 0)) return false;
  const sca = parseDmy(m.DtaSca);
  if (!sca) return false;
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  return sca < oggi;
};

// Cella data (scadenza/pagamento) modificabile: scrive direttamente su Segremat.
function EditableDateCell({ m, field, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(dmyToIso(m[field] || ""));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(dmyToIso(m[field] || "")); }, [m[field], m.Num, m.anno]);

  if (!editing) {
    return (
      <span data-testid={`mov-date-${field}-${m.Num}`} onClick={() => setEditing(true)}
        title="Clicca per modificare la data su Segremat"
        style={{ cursor: "pointer", borderBottom: "1px dashed #90a4ae", whiteSpace: "nowrap" }}>
        {m[field] || "—"} <span style={{ fontSize: 10, color: "#1565c0" }}>✎</span>
      </span>
    );
  }
  const save = async () => {
    const dmy = isoToDmy(val);
    if (!dmy) { toast.error("Seleziona una data valida"); return; }
    setSaving(true);
    const ok = await onSave(m, field, dmy);
    setSaving(false);
    if (ok) setEditing(false);
  };
  const cancel = () => { setVal(dmyToIso(m[field] || "")); setEditing(false); };
  return (
    <span style={{ display: "inline-flex", gap: 2, alignItems: "center", whiteSpace: "nowrap" }}>
      <input type="date" value={val} disabled={saving} onChange={e => setVal(e.target.value)}
        data-testid={`mov-date-input-${field}-${m.Num}`}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
        style={{ padding: "3px 5px", border: "1px solid #1565c0", fontSize: 12, fontFamily: "inherit" }} />
      <button onClick={save} disabled={saving} data-testid={`mov-date-save-${field}-${m.Num}`}
        title="Salva su Segremat" style={{ border: "none", background: "#2e7d32", color: "white", cursor: saving ? "wait" : "pointer", fontWeight: 800, padding: "3px 6px", borderRadius: 3 }}>✓</button>
      <button onClick={cancel} disabled={saving} title="Annulla" data-testid={`mov-date-cancel-${field}-${m.Num}`}
        style={{ border: "none", background: "#eceff1", color: "#555", cursor: "pointer", fontWeight: 800, padding: "3px 6px", borderRadius: 3 }}>✕</button>
    </span>
  );
}

export default function MovimentiPage() {
  const { annoCorrente } = useAuth();
  const navigate = useNavigate();
  const { gestione } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Filtro per codice alunno (passato da altre pagine via URL ?codice_alunno=X&anno=Y)
  const codiceAlunnoUrl = searchParams.get("codice_alunno");
  const annoUrl = searchParams.get("anno");
  const fromTab = searchParams.get("from"); // "alunni" | "alunni_agev" | null
  const backUrl = searchParams.get("back"); // URL completo di ritorno (es. /g/comunicator/comunicator)
  const backLabel = searchParams.get("back_label") || "Indietro";
  const [codiceAlunno, setCodiceAlunno] = useState(codiceAlunnoUrl ? parseInt(codiceAlunnoUrl, 10) : null);
  // Ordinamento: default sempre Scadenza (crescente)
  const [sortBy, setSortBy] = useState("DtaSca");
  const [sortDir, setSortDir] = useState("asc");
  const [alunnoInfo, setAlunnoInfo] = useState(null); // { Cog, Nom, classe, settore }
  const [movimenti, setMovimenti] = useState([]);
  const [total, setTotal] = useState(0);
  const [totaleImporto, setTotaleImporto] = useState(0);
  const [totalePagati, setTotalePagati] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const fetchIdRef = useRef(0);
  const [localAnno, setLocalAnno] = useState(null);
  const [anniList, setAnniList] = useState([]);
  const limit = 5000;  // No pagination: tutta la lista sempre caricata (richiesta utente)
  const [settoreFilter, setSettoreFilter] = useState("");
  const [classeFilter, setClasseFilter] = useState("");
  const [causaleFilter, setCausaleFilter] = useState("");
  const [modalitaFilter, setModalitaFilter] = useState("");
  const [descrizioneFilter, setDescrizioneFilter] = useState("");
  const [annoCompFilter, setAnnoCompFilter] = useState("");
  const [tipoDocFilter, setTipoDocFilter] = useState("");
  const [marcaturaFilter, setMarcaturaFilter] = useState(""); // '' | 'dote' | 'escluso' | 'nessuna'
  const [statoPagFilter, setStatoPagFilter] = useState(""); // '' | 'pagati' | 'non_pagati'
  const [daScadenza, setDaScadenza] = useState("");
  const [aScadenza, setAScadenza] = useState("");
  const [daPagamento, setDaPagamento] = useState("");
  const [aPagamento, setAPagamento] = useState("");
  const [filtri, setFiltri] = useState({ settori: [], classi: [], causali: [], modalita: [], tipi_doc: [], anni_competenza: [], descrizioni: [] });

  useEffect(() => {
    (async () => {
      try {
        const res = await getAnniDisponibili();
        const anni = res.data.anni || [];
        setAnniList(anni);
        const annoFromUrl = annoUrl ? parseInt(annoUrl, 10) : null;
        if (annoFromUrl && anni.includes(annoFromUrl)) {
          setLocalAnno(annoFromUrl);
        } else {
          setLocalAnno(anni.includes(annoCorrente) ? annoCorrente : (anni[0] || null));
        }
      } catch (e) { console.error(e); }
    })();
  }, [annoCorrente, annoUrl]);

  // Carica info anagrafica alunno se filtro attivo
  useEffect(() => {
    if (!codiceAlunno || !localAnno) { setAlunnoInfo(null); return; }
    (async () => {
      try {
        const r = await api.get("/alunni", { params: { anno: localAnno, search: String(codiceAlunno), limit: 1 } });
        const a = (r.data?.data || []).find(x => x.Cod === codiceAlunno) || (r.data?.data || [])[0];
        if (a) setAlunnoInfo({ Cog: a.Cog, Nom: a.Nom, classe: a.classe, settore: a.settore, CodFis: a.CodFis });
      } catch (e) { /* ignore */ }
    })();
  }, [codiceAlunno, localAnno]);

  useEffect(() => {
    (async () => {
      // Carica TUTTI i filtri (senza restringere a un anno scolastico), così l'utente
      // vede in elenco tutti gli anni di competenza disponibili.
      try { const res = await getFiltriMovimenti(null); setFiltri(res.data); }
      catch (e) { console.error(e); }
    })();
  }, []);

  // Debounce ricerca: aggiorna il filtro reale 350ms dopo l'ultima digitazione
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    if (!localAnno) { setMovimenti([]); setTotal(0); setLoading(false); return; }
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const params = { page, limit };
      // Anno: il filtro "anno comp." (annoCompFilter) determina il vincolo sull'anno.
      // - Se l'utente ha scelto un anno specifico in "anno comp." → filtra per quell'anno.
      // - Se l'utente ha scelto "Tutti gli anni" (annoCompFilter === "") → NESSUN filtro
      //   sull'anno, mostra movimenti di tutti gli anni.
      // L'anno scolastico globale (localAnno) viene applicato come default SOLO quando
      // il dropdown "anno comp." è vuoto in modo non esplicito (al primo caricamento).
      // Per rispettare la scelta esplicita "Tutti gli anni" non si applica `anno`.
      if (annoCompFilter) {
        params.anno_competenza = parseInt(annoCompFilter);
      }
      if (search) params.search = search;
      if (settoreFilter) params.settore = settoreFilter;
      if (classeFilter) params.classe_nome = classeFilter;
      if (causaleFilter) params.causale = causaleFilter;
      if (modalitaFilter) params.modalita = modalitaFilter;
      if (descrizioneFilter) params.descrizione = descrizioneFilter;
      if (annoCompFilter) params.anno_competenza = parseInt(annoCompFilter);
      if (tipoDocFilter) params.tipo_doc = tipoDocFilter;
      if (daScadenza) params.da_data_scadenza = daScadenza;
      if (aScadenza) params.a_data_scadenza = aScadenza;
      if (daPagamento) params.da_data_pagamento = daPagamento;
      if (aPagamento) params.a_data_pagamento = aPagamento;
      if (marcaturaFilter) params.marcatura = marcaturaFilter;
      if (statoPagFilter) params.stato_pagamento = statoPagFilter;
      if (codiceAlunno) params.codice_alunno = codiceAlunno;
      if (sortBy) params.sort_by = sortBy;
      if (sortDir) params.sort_dir = sortDir;
      const res = await getMovimenti(params);
      if (fetchId !== fetchIdRef.current) return; // risposta obsoleta: ignora
      setMovimenti(res.data.data); setTotal(res.data.total);
      setTotaleImporto(res.data.totale_importo || 0);
      setTotalePagati(res.data.totale_pagati || 0);
    } catch (e) { console.error(e); }
    finally { if (fetchId === fetchIdRef.current) setLoading(false); }
  }, [page, search, settoreFilter, classeFilter, causaleFilter, modalitaFilter, descrizioneFilter, annoCompFilter, tipoDocFilter, daScadenza, aScadenza, daPagamento, aPagamento, marcaturaFilter, statoPagFilter, codiceAlunno, localAnno, limit, sortBy, sortDir]);

  useEffect(() => { setPage(1); }, [search, settoreFilter, classeFilter, causaleFilter, modalitaFilter, descrizioneFilter, annoCompFilter, tipoDocFilter, daScadenza, aScadenza, daPagamento, aPagamento, marcaturaFilter, statoPagFilter, codiceAlunno, localAnno]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Salva (debounce) marcatura/nota_locale per un movimento. Aggiorna lo state in ottimistico.
  const updateMarcatura = async (m, patch) => {
    const newRow = { ...m, ...patch };
    setMovimenti(prev => prev.map(x => (x.anno === m.anno && x.Num === m.Num) ? newRow : x));
    try {
      await setMovimentoMarcatura(m.anno, m.Num, {
        marcatura: newRow.marcatura || null,
        nota_locale: newRow.nota_locale || "",
      });
    } catch (e) { console.error("marcatura save failed", e); }
  };

  // Modifica data scadenza/pagamento di un movimento scrivendo su Segremat.
  const saveDate = async (m, field, dmy) => {
    const body = { anno: m.anno, codice_alunno: m.Alu, codice_movimento: m.Num };
    if (field === "DtaSca") body.data_scadenza = dmy;
    else body.data_pagamento = dmy;
    try {
      const r = await api.post("/riconciliazione/modifica-date", body);
      setMovimenti(prev => prev.map(x => (x.anno === m.anno && x.Num === m.Num)
        ? { ...x, DtaSca: r.data.data_scadenza ?? x.DtaSca, DtaPag: r.data.data_pagamento ?? x.DtaPag } : x));
      toast.success(`Movimento ${m.Num}: data aggiornata su Segremat`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento data su Segremat");
      return false;
    }
  };

  // Modifica date in blocco (sui movimenti attualmente in lista/filtrati)
  const [bulkOpen, setBulkOpen] = useState(false);
  // Invio solleciti di pagamento (sui movimenti NON pagati attualmente filtrati)
  const [sollecitiOpen, setSollecitiOpen] = useState(false);
  const nonPaidItems = movimenti.filter(m => !m.DtaPag && Number(m.Imp) > 0);

  // Marcatore automatico: azzera le marcature dell'anno e ri-applica in base al testo della descrizione/causale/note
  const [marcatoreRun, setMarcatoreRun] = useState(false);
  const lanciaMarcatore = async () => {
    if (!localAnno) { alert("Seleziona prima un anno."); return; }
    const ok = window.confirm(
      `🏷️ Marcatore automatico per l'anno ${localAnno}-${String(localAnno+1).slice(2)}\n\n` +
      `Verranno PRIMA AZZERATE tutte le marcature DOTE/ESCLUSO esistenti per quest'anno,\n` +
      `poi reimpostate in base al testo dei movimenti:\n` +
      `  • DOTE     → se la descrizione contiene "dote"\n` +
      `  • ESCLUSO  → se la descrizione contiene "non dovuto" o "escluso"\n\n` +
      `Le note locali NON verranno toccate.\nProcedere?`
    );
    if (!ok) return;
    setMarcatoreRun(true);
    try {
      const r = await runMarcatoreAuto(localAnno);
      alert(`✅ Marcatore completato:\n\n` +
        `Movimenti scansionati: ${r.data.scansionati}\n` +
        `Marcati DOTE: ${r.data.marcati_dote}\n` +
        `Marcati ESCLUSO: ${r.data.marcati_escluso}\n` +
        `Totale marcati: ${r.data.totale_marcati}`);
      await fetchData();
    } catch (e) {
      alert("Errore durante il marcatore: " + (e.response?.data?.detail || e.message));
    } finally { setMarcatoreRun(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const hasFilters = settoreFilter||classeFilter||causaleFilter||modalitaFilter||descrizioneFilter||annoCompFilter||tipoDocFilter||search||daScadenza||aScadenza||daPagamento||aPagamento||marcaturaFilter||statoPagFilter;
  const clearAll = () => { setSettoreFilter(""); setClasseFilter(""); setCausaleFilter(""); setModalitaFilter(""); setDescrizioneFilter(""); setAnnoCompFilter(""); setTipoDocFilter(""); setSearch(""); setSearchInput(""); setDaScadenza(""); setAScadenza(""); setDaPagamento(""); setAPagamento(""); setMarcaturaFilter(""); setStatoPagFilter(""); };
  const docLabels = { "F": "Fattura", "R": "Ricevuta", "N": "Nota" };

  const removeAlunnoFilter = () => {
    setCodiceAlunno(null);
    setAlunnoInfo(null);
    const sp = new URLSearchParams(searchParams);
    sp.delete("codice_alunno");
    sp.delete("anno");
    setSearchParams(sp, { replace: true });
  };

  // Modale stampa estratto conto
  const [estrattoModalOpen, setEstrattoModalOpen] = useState(false);

  // Stampa lista movimenti — visibile solo da SegrematWeb Pro (/g/segremat/movimenti)
  const location = useLocation();
  const isSegremat = location.pathname.includes("/g/segremat/");
  const [stampaLoading, setStampaLoading] = useState(false);

  const stampaListaMovimenti = async () => {
    setStampaLoading(true);
    try {
      // Riusa gli stessi filtri della pagina, ma scarica TUTTI i risultati (no paginazione)
      const params = { page: 1, limit: 10000 };
      if (search) params.search = search;
      if (settoreFilter) params.settore = settoreFilter;
      if (classeFilter) params.classe_nome = classeFilter;
      if (causaleFilter) params.causale = causaleFilter;
      if (modalitaFilter) params.modalita = modalitaFilter;
      if (annoCompFilter) params.anno_competenza = parseInt(annoCompFilter);
      if (tipoDocFilter) params.tipo_doc = tipoDocFilter;
      if (daScadenza) params.da_data_scadenza = daScadenza;
      if (aScadenza) params.a_data_scadenza = aScadenza;
      if (daPagamento) params.da_data_pagamento = daPagamento;
      if (aPagamento) params.a_data_pagamento = aPagamento;
      if (marcaturaFilter) params.marcatura = marcaturaFilter;
      if (statoPagFilter) params.stato_pagamento = statoPagFilter;
      if (codiceAlunno) params.codice_alunno = codiceAlunno;
      const res = await getMovimenti(params);
      const all = res.data?.data || [];
      const totImp = res.data?.totale_importo || 0;
      const totPag = res.data?.totale_pagati || 0;
      // Costruisco l'elenco testuale dei filtri attivi
      const filtriAttivi = [];
      if (search) filtriAttivi.push(`Ricerca: "${search}"`);
      if (annoCompFilter) filtriAttivi.push(`Anno comp.: ${annoCompFilter}`);
      if (settoreFilter) filtriAttivi.push(`Settore: ${settoreFilter}`);
      if (classeFilter) filtriAttivi.push(`Classe: ${classeFilter}`);
      if (causaleFilter) filtriAttivi.push(`Causale: ${causaleFilter}`);
      if (modalitaFilter) filtriAttivi.push(`Modalità: ${modalitaFilter}`);
      if (tipoDocFilter) filtriAttivi.push(`Tipo doc: ${docLabels[tipoDocFilter] || tipoDocFilter}`);
      if (statoPagFilter) filtriAttivi.push(`Stato pag.: ${statoPagFilter}`);
      if (marcaturaFilter) filtriAttivi.push(`Marcatura: ${marcaturaFilter}`);
      if (daScadenza || aScadenza) filtriAttivi.push(`Scadenza: ${daScadenza || "—"} → ${aScadenza || "—"}`);
      if (daPagamento || aPagamento) filtriAttivi.push(`Pagamento: ${daPagamento || "—"} → ${aPagamento || "—"}`);
      if (codiceAlunno) filtriAttivi.push(`Alunno: ${alunnoInfo ? `${alunnoInfo.Cog} ${alunnoInfo.Nom}` : ""} (cod. ${codiceAlunno})`);
      const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      const rows = all.map((m, i) => `<tr>
        <td class="c muted">${i + 1}</td>
        <td class="c">${esc(m.anno ? `${String(m.anno).slice(-2)}/${String(m.anno + 1).slice(-2)}` : "—")}</td>
        <td class="c">${esc(m.Num)}</td>
        <td><b>${esc(m.alu_nome || m.Alu)}</b><div class="sub">cod. ${esc(m.Alu)}</div></td>
        <td class="c">${esc(m.alu_settore || "-")}</td>
        <td class="c">${esc(m.alu_classe || "-")}</td>
        <td class="c">${esc(m.Cau ?? "-")}</td>
        <td>${esc(m.CauDes || "")}</td>
        <td class="wrap">${esc(m.Des || "")}</td>
        <td class="c">${esc(m.DtaSca || "-")}</td>
        <td class="c">${esc(m.DtaPag || "-")}</td>
        <td class="num">${eur(m.Imp)}</td>
        <td class="c">${esc(m.ModDes || m.Mod || "-")}</td>
        <td class="c">${esc(m.marcatura ? m.marcatura.toUpperCase() : "")}</td>
      </tr>`).join("");
      const oggi = new Date().toLocaleString("it-IT");
      const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Movimenti — Lista stampa</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; font-size: 9pt; color: #111; margin: 0; padding: 0; }
  h1 { font-size: 16pt; margin: 0 0 4px 0; color: #0d47a1; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0d47a1; padding-bottom: 6px; margin-bottom: 8px; }
  .meta { font-size: 8.5pt; color: #555; }
  .filtri { background: #f5f5f5; padding: 6px 8px; font-size: 8pt; margin-bottom: 8px; border-left: 3px solid #1565c0; }
  .filtri b { color: #0d47a1; }
  table { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
  th { background: #1f3864; color: white; padding: 4px 5px; text-align: left; font-weight: 700; border: 1px solid #1f3864; }
  td { padding: 3px 5px; border: 1px solid #ddd; vertical-align: top; }
  .c { text-align: center; }
  .num { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
  .wrap { white-space: normal; word-break: break-word; }
  .small { font-size: 8pt; color: #555; }
  .muted { color: #888; }
  .sub { font-size: 7.5pt; color: #666; margin-top: 1px; }
  tr:nth-child(even) td { background: #fafafa; }
  .totals { margin-top: 10px; display: flex; justify-content: flex-end; gap: 24px; font-size: 10pt; padding: 6px 12px; background: #e8eaf6; border: 1px solid #c5cae9; }
  .totals b { color: #0d47a1; }
  .footer { margin-top: 8px; text-align: right; font-size: 7.5pt; color: #888; }
  @media print { .no-print { display: none; } }
  .no-print { padding: 10px; text-align: center; background: #fff3e0; border-bottom: 1px solid #ffcc80; position: sticky; top: 0; z-index: 10; }
  .no-print button { padding: 6px 18px; background: #1565c0; color: white; border: none; font-weight: 700; cursor: pointer; font-size: 12px; }
</style>
</head>
<body>
  <div class="no-print">
    <button onclick="window.print()">🖨 Stampa</button>
    &nbsp;&nbsp;<button onclick="window.close()" style="background:#666">Chiudi</button>
  </div>
  <div class="header">
    <div>
      <h1>📋 Lista Movimenti — SegrematWeb Pro</h1>
      <div class="meta">Stampato il ${esc(oggi)} · ${all.length} movimenti</div>
    </div>
    <div class="meta" style="text-align: right">
      <div><b>canossaHUB</b></div>
      <div>Istituto Canossiano · Brescia</div>
    </div>
  </div>
  ${filtriAttivi.length > 0 ? `<div class="filtri"><b>Filtri attivi:</b> ${filtriAttivi.map(esc).join(" · ")}</div>` : ""}
  <table>
    <thead><tr>
      <th style="width:30px">#</th>
      <th style="width:50px">A.Comp.</th>
      <th style="width:60px">Num</th>
      <th>Alunno</th>
      <th style="width:35px">Sett.</th>
      <th style="width:50px">Classe</th>
      <th style="width:35px">Cau</th>
      <th style="width:100px">CauDes</th>
      <th>Descrizione</th>
      <th style="width:65px">Scad.</th>
      <th style="width:65px">Pag.</th>
      <th style="width:75px">Importo</th>
      <th style="width:80px">Modalità</th>
      <th style="width:55px">Marcatura</th>
    </tr></thead>
    <tbody>${rows || '<tr><td colspan="14" style="text-align:center;padding:14px;color:#888">Nessun movimento</td></tr>'}</tbody>
  </table>
  <div class="totals">
    <div>📊 Totale movimenti: <b>${all.length.toLocaleString("it-IT")}</b></div>
    <div>💰 Totale importo: <b>€ ${eur(totImp)}</b></div>
    <div>✓ Pagati: <b>€ ${eur(totPag)}</b></div>
  </div>
  <div class="footer">canossaHUB — Movimenti SegrematWeb Pro</div>
</body></html>`;
      const w = openPrintWindow(html);
      if (!w) { alert("Abilita i popup per la stampa."); return; }
    } catch (e) {
      alert("Errore stampa: " + (e.response?.data?.detail || e.message));
    } finally {
      setStampaLoading(false);
    }
  };

  return (
    <div data-testid="movimenti-page">
      <div className="sw-page-title">
        {(fromTab || backUrl) && (
          <button onClick={() => navigate(backUrl || `/g/${gestione}/${fromTab}`)} data-testid="mov-back-to-alunni"
            title={backUrl ? backLabel : (fromTab === "alunni_agev" ? "Torna alla lista Alunni Con Agevolazione" : "Torna alla lista Alunni")}
            style={{
              marginRight: 12, padding: "7px 14px",
              background: "linear-gradient(180deg, #455a64 0%, #263238 100%)", color: "white",
              border: "1px solid #1c2a30", fontSize: 12, fontWeight: 800, letterSpacing: 0.4,
              cursor: "pointer", borderRadius: 3, boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            }}>
            ← {backUrl ? backLabel : `Torna a ${fromTab === "alunni_agev" ? "Con Agevol." : "Alunni"}`}
          </button>
        )}
        <h1>Movimenti</h1>
        <div className="sw-breadcrumb">Home &gt; Gestione pagamenti &gt; Movimenti</div>
        {isSegremat && (
          <button onClick={stampaListaMovimenti} disabled={stampaLoading || loading} data-testid="mov-stampa-lista"
            title="Stampa la lista dei movimenti correntemente filtrati"
            style={{
              marginLeft: "auto",
              padding: "8px 18px",
              background: stampaLoading ? "#90a4ae" : "linear-gradient(180deg, #1976d2 0%, #0d47a1 100%)",
              color: "white",
              border: "2px solid #0d47a1",
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: 0.5,
              cursor: stampaLoading ? "wait" : "pointer",
              boxShadow: "0 2px 8px rgba(13,71,161,0.45)",
              textTransform: "uppercase",
            }}>
            {stampaLoading ? "Preparazione..." : "🖨 Stampa Lista"}
          </button>
        )}
        {isSegremat && (
          <button onClick={() => setBulkOpen(true)} disabled={loading || movimenti.length === 0} data-testid="mov-bulk-date-btn"
            title="Cambia la data di scadenza e/o pagamento su tutti i movimenti attualmente in lista"
            style={{ marginLeft: 8, padding: "8px 18px", background: "linear-gradient(180deg,#00897b 0%,#00695c 100%)", color: "white", border: "2px solid #004d40", fontSize: 13, fontWeight: 800, letterSpacing: 0.5, cursor: (loading || movimenti.length === 0) ? "not-allowed" : "pointer", boxShadow: "0 2px 8px rgba(0,105,92,0.45)", textTransform: "uppercase" }}>
            🗓 Modifica date in blocco
          </button>
        )}
        {isSegremat && (
          <button onClick={() => setSollecitiOpen(true)} disabled={loading || nonPaidItems.length === 0} data-testid="mov-solleciti-btn"
            title={descrizioneFilter ? `Invia un sollecito di pagamento alle famiglie dei ${nonPaidItems.length} movimenti non pagati in lista` : "Suggerito: filtra prima per una descrizione. Invia un sollecito ai movimenti non pagati in lista"}
            style={{ marginLeft: 8, padding: "8px 18px", background: (loading || nonPaidItems.length === 0) ? "#b0bec5" : "linear-gradient(180deg,#8e24aa 0%,#6a1b9a 100%)", color: "white", border: "2px solid #4a148c", fontSize: 13, fontWeight: 800, letterSpacing: 0.5, cursor: (loading || nonPaidItems.length === 0) ? "not-allowed" : "pointer", boxShadow: "0 2px 8px rgba(106,27,154,0.45)", textTransform: "uppercase" }}>
            📧 Invia Solleciti{nonPaidItems.length ? ` (${nonPaidItems.length})` : ""}
          </button>
        )}
        <button onClick={lanciaMarcatore} disabled={marcatoreRun || !localAnno} data-testid="mov-marcatore-btn"
          title="Esegue il marcatore automatico (DOTE / ESCLUSO) sull'anno selezionato"
          style={{
            marginLeft: isSegremat ? 8 : "auto",
            padding: "8px 18px",
            background: marcatoreRun ? "#90a4ae" : "linear-gradient(180deg, #f9a825 0%, #ef6c00 100%)",
            color: "white",
            border: "2px solid #c43e00",
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 0.5,
            cursor: marcatoreRun ? "wait" : "pointer",
            boxShadow: "0 2px 8px rgba(239,108,0,0.45)",
            textTransform: "uppercase",
          }}>
          {marcatoreRun ? "Eseguo..." : "🏷️ Marcatore"}
        </button>
      </div>

      {codiceAlunno && (
        <div data-testid="mov-banner-alunno"
          style={{ margin: "8px 0", padding: "10px 14px", background: "#e3f2fd", border: "2px solid #1565c0", borderRadius: 4, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "#0d47a1" }}>
            🎯 <b>Filtro alunno attivo:</b>{" "}
            {alunnoInfo
              ? <span><b style={{ fontSize: 14 }}>{alunnoInfo.Cog} {alunnoInfo.Nom}</b> · cod. {codiceAlunno}{alunnoInfo.settore ? ` · ${alunnoInfo.settore}` : ""}{alunnoInfo.classe ? ` ${alunnoInfo.classe}` : ""}{alunnoInfo.CodFis ? ` · CF: ${alunnoInfo.CodFis}` : ""}</span>
              : <span>cod. {codiceAlunno}</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setEstrattoModalOpen(true)} data-testid="mov-stampa-estratto"
              style={{ padding: "7px 14px", background: "#7b1fa2", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
              🖨️ Stampa Estratto Conto
            </button>
            <button onClick={removeAlunnoFilter} data-testid="mov-rimuovi-filtro-alunno"
              style={{ padding: "7px 12px", background: "white", color: "#1565c0", border: "1px solid #1565c0", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              ✕ Rimuovi filtro
            </button>
          </div>
        </div>
      )}

      {estrattoModalOpen && (
        <EstrattoContoModal codiceAlunno={codiceAlunno} anno={localAnno} alunnoInfo={alunnoInfo}
          onClose={() => setEstrattoModalOpen(false)} />
      )}

      {bulkOpen && (
        <BulkDateModal movimenti={movimenti}
          onClose={() => setBulkOpen(false)}
          onDone={async () => { setBulkOpen(false); await fetchData(); }} />
      )}

      {sollecitiOpen && (
        <SollecitiModal
          items={nonPaidItems.map(m => ({ anno: m.anno, Num: m.Num }))}
          descrizione={descrizioneFilter}
          onClose={() => setSollecitiOpen(false)} />
      )}

      <div className="sw-filter-box">
        <div className="sw-filter-row" style={{flexWrap:"wrap", gap:6}}>
          <span className="sw-filter-label" style={{ fontWeight: 700, color: "#7b1fa2" }} title="Anno di competenza = archivio/esercizio Segremat del movimento (campo 'anno'). È il valore della prima colonna 'A.Comp.'. Es: 2026 → AS 26/27.">anno comp.:</span>
          <select data-testid="mov-anno-comp-filter" value={annoCompFilter} onChange={e=>setAnnoCompFilter(e.target.value)}
            title="Filtra i movimenti per anno di competenza (prima colonna della tabella)"
            className="sw-filter-select" style={{ width: 110, background: annoCompFilter ? "#f3e5f5" : "white", border: "2px solid #7b1fa2", fontWeight: 700 }}>
            <option value="">Tutti gli anni</option>
            {filtri.anni_competenza.map(a => {
              const label = `${a} (${String(a).slice(-2)}/${String(a+1).slice(-2)})`;
              return <option key={a} value={a}>{label}</option>;
            })}
          </select>
          <span className="sw-filter-label">settore:</span>
          <select data-testid="mov-settore-filter" value={settoreFilter} onChange={e=>setSettoreFilter(e.target.value)} className="sw-filter-select" style={{width:80}}>
            <option value="">Qualsiasi</option>
            {filtri.settori.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <span className="sw-filter-label">classe:</span>
          <select data-testid="mov-classe-filter" value={classeFilter} onChange={e=>setClasseFilter(e.target.value)} className="sw-filter-select" style={{width:90}}>
            <option value="">Qualsiasi</option>
            {filtri.classi.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <span className="sw-filter-label">causale:</span>
          <select data-testid="mov-causale-filter" value={causaleFilter} onChange={e=>setCausaleFilter(e.target.value)} className="sw-filter-select" style={{width:130}}>
            <option value="">Qualsiasi</option>
            {filtri.causali.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <span className="sw-filter-label">modalità:</span>
          <select data-testid="mov-modalita-filter" value={modalitaFilter} onChange={e=>setModalitaFilter(e.target.value)} className="sw-filter-select" style={{width:120}}>
            <option value="">Qualsiasi</option>
            {filtri.modalita.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <span className="sw-filter-label">stato pag.:</span>
          <select data-testid="mov-stato-pag-filter" value={statoPagFilter} onChange={e=>setStatoPagFilter(e.target.value)}
            title="Filtra per stato pagamento: pagati = ha data pagamento; non pagati = senza data pagamento"
            className="sw-filter-select" style={{width:130, background: statoPagFilter ? (statoPagFilter==="pagati" ? "#e8f5e9" : "#ffebee") : "white", fontWeight: statoPagFilter ? 700 : 400}}>
            <option value="">Tutti</option>
            <option value="pagati">✓ Pagati</option>
            <option value="non_pagati">✗ Non pagati</option>
          </select>
          <span className="sw-filter-label">tipo doc.:</span>
          <select data-testid="mov-tipo-doc-filter" value={tipoDocFilter} onChange={e=>setTipoDocFilter(e.target.value)} className="sw-filter-select" style={{width:100}}>
            <option value="">Qualsiasi</option>
            {filtri.tipi_doc.map(t=><option key={t} value={t}>{docLabels[t]||t}</option>)}
          </select>
          <span className="sw-filter-label" style={{fontWeight:700, color:"#00695c"}} title="Filtra tutti i movimenti con questa esatta descrizione">descrizione:</span>
          <select data-testid="mov-descrizione-filter" value={descrizioneFilter} onChange={e=>setDescrizioneFilter(e.target.value)}
            title="Filtra tutti i movimenti con questa esatta descrizione"
            className="sw-filter-select" style={{width:300, maxWidth:"40vw", background: descrizioneFilter ? "#e0f2f1" : "white", border:"2px solid #00695c", fontWeight: descrizioneFilter ? 700 : 400}}>
            <option value="">Tutte le descrizioni</option>
            {(filtri.descrizioni||[]).map(d=><option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="sw-filter-row" style={{flexWrap:"wrap", gap:6}}>
          <span className="sw-filter-label">scad. dal:</span>
          <input data-testid="mov-da-scadenza" type="date" value={dmyToIso(daScadenza)} onChange={e=>setDaScadenza(isoToDmy(e.target.value))} className="sw-filter-input" style={{width:140}} />
          <span className="sw-filter-label">al:</span>
          <input data-testid="mov-a-scadenza" type="date" value={dmyToIso(aScadenza)} onChange={e=>setAScadenza(isoToDmy(e.target.value))} className="sw-filter-input" style={{width:140}} />
          <span className="sw-filter-label" style={{marginLeft:16}}>pag. dal:</span>
          <input data-testid="mov-da-pagamento" type="date" value={dmyToIso(daPagamento)} onChange={e=>setDaPagamento(isoToDmy(e.target.value))} className="sw-filter-input" style={{width:140}} />
          <span className="sw-filter-label">al:</span>
          <input data-testid="mov-a-pagamento" type="date" value={dmyToIso(aPagamento)} onChange={e=>setAPagamento(isoToDmy(e.target.value))} className="sw-filter-input" style={{width:140}} />
          <span className="sw-filter-label" style={{marginLeft:16}}>marcatura:</span>
          <select data-testid="mov-marcatura-filter" value={marcaturaFilter} onChange={e=>setMarcaturaFilter(e.target.value)} className="sw-filter-select" style={{width:120, background:"#fff8e1", fontWeight:700}}>
            <option value="">Tutti</option>
            <option value="dote">DOTE</option>
            <option value="escluso">ESCLUSO</option>
            <option value="nessuna">Senza marcatura</option>
          </select>
          {/* 🆕 Feb 2026: dropdown Ordinamento con opzioni "per scadenza / per pagamento / per anno+scadenza / N.Mov" */}
          <span className="sw-filter-label" style={{marginLeft:16, fontWeight:700, color:"#0d47a1"}}>ordina per:</span>
          <select data-testid="mov-sort-selector"
            value={`${sortBy}-${sortDir}`}
            onChange={e => {
              const [sb, sd] = e.target.value.split("-");
              setSortBy(sb);
              setSortDir(sd);
            }}
            title="Cambia criterio di ordinamento della tabella"
            className="sw-filter-select"
            style={{width:210, background:"#e3f2fd", border:"2px solid #0d47a1", fontWeight:700, color:"#0d47a1"}}>
            <option value="DtaSca-asc">📅 Scadenza (crescente)</option>
            <option value="DtaSca-desc">📅 Scadenza (decrescente)</option>
            <option value="DtaPag-desc">💰 Pagamento (più recenti)</option>
            <option value="DtaPag-asc">💰 Pagamento (più vecchi)</option>
            <option value="AnnCom_DtaSca-asc">🎓 A.scol. → Scadenza</option>
            <option value="Num-desc">🔢 N.Mov (default)</option>
          </select>
          <span className="sw-filter-label" style={{marginLeft:16}}>cerca:</span>
          <input data-testid="search-movimenti" type="text" placeholder="alunno, descrizione, note..." value={searchInput} onChange={e=>setSearchInput(e.target.value)} className="sw-filter-input" style={{width:240}} />
          {hasFilters && <button className="sw-filter-btn" onClick={clearAll}>Pulisci</button>}
        </div>
      </div>

      <div style={{margin:"4px 20px",fontSize:12,color:"#666",display:"flex",gap:24,alignItems:"center",flexWrap:"wrap"}}>
        <span data-testid="mov-total-count">{total.toLocaleString("it-IT")} movimenti trovati</span>
        <span style={{color:"#37474f",fontWeight:700}} data-testid="mov-total-importo">Totale movimenti: <b>{formatCurrency(totaleImporto)}</b></span>
        <span style={{color:"#1b5e20",fontWeight:700}} data-testid="mov-total-pagati">Totale pagati: <b>{formatCurrency(totalePagati)}</b></span>
        <span style={{color:"#ef6c00",fontWeight:700}} data-testid="mov-total-non-pagati">Da incassare: <b>{formatCurrency(totaleImporto - totalePagati)}</b></span>
      </div>

      <div className="sw-table-container">
        <table className="sw-table">
          <thead>
            <tr>
              <th style={{width:30}}>#</th>
              <th>A.Comp.</th>
              <th style={{cursor:"pointer", userSelect:"none"}} title="Clicca per ordinare per N.Mov"
                onClick={() => { setSortBy("Num"); setSortDir(sortBy === "Num" && sortDir === "desc" ? "asc" : "desc"); }}>
                N.Mov{sortBy === "Num" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
              <th>Alunno</th>
              <th>Sett.</th>
              <th>Classe</th>
              <th style={{width:60, textAlign:"center"}}>Cod.Cau</th>
              <th>Causale</th>
              <th style={{minWidth:200}}>Descrizione</th>
              <th style={{cursor:"pointer", userSelect:"none"}} title="Clicca per ordinare per data scadenza"
                onClick={() => { setSortBy("DtaSca"); setSortDir(sortBy === "DtaSca" && sortDir === "asc" ? "desc" : "asc"); }}>
                Scadenza{sortBy === "DtaSca" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
              <th style={{cursor:"pointer", userSelect:"none"}} title="Clicca per ordinare per data pagamento"
                onClick={() => { setSortBy("DtaPag"); setSortDir(sortBy === "DtaPag" && sortDir === "asc" ? "desc" : "asc"); }}>
                Pagamento{sortBy === "DtaPag" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
              <th style={{textAlign:"right"}}>Importo</th>
              <th style={{width:60, textAlign:"center"}}>Cod.Mod</th>
              <th>Mod.Pag.</th>
              <th style={{width:140, background:"#fff8e1"}}>Marcatura</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={15} className="sw-loading">Caricamento...</td></tr>
            ) : movimenti.length === 0 ? (
              <tr><td colSpan={15} className="sw-empty"><p>Nessun movimento trovato</p></td></tr>
            ) : movimenti.map((m, idx) => {
              const scadNonPag = isScadutoNonPagato(m);
              return (
              <tr key={`${m.Num}-${idx}`} data-testid={`movimento-row-${m.Num}`}
                style={scadNonPag ? { background: "#ffebee" } : undefined}>
                <td className="center-col" style={{color:"#888"}}>{(page-1)*limit+idx+1}</td>
                <td style={{ fontWeight: 700, color: "#7b1fa2" }}>
                  {(() => {
                    const a = m.anno;
                    if (a == null) return "—";
                    return `${String(a).slice(-2)}/${String(a+1).slice(-2)}`;
                  })()}
                </td>
                <td>{m.Num}</td>
                <td style={{fontWeight:600}} title={`Cod. ${m.Alu}`}>{m.alu_nome||m.Alu}</td>
                <td className="center-col">{m.alu_settore||"-"}</td>
                <td>{m.alu_classe||"-"}</td>
                <td className="center-col" style={{fontWeight:700, fontFamily:"Consolas", color:"#0d47a1"}}>{m.Cau ?? "-"}</td>
                <td>{m.CauDes}</td>
                <td style={{whiteSpace:"normal",wordBreak:"break-word",minWidth:200}}>{m.Des||"-"}</td>
                <td><EditableDateCell m={m} field="DtaSca" onSave={saveDate} /></td>
                <td><EditableDateCell m={m} field="DtaPag" onSave={saveDate} /></td>
                <td className="num-col" style={{fontWeight:scadNonPag?800:600, color:scadNonPag?"#c62828":undefined}}>{formatCurrency(m.Imp)}</td>
                <td className="center-col" style={{fontWeight:700, fontFamily:"Consolas", color:"#0d47a1"}}>{m.Mod ?? "-"}</td>
                <td>{m.ModDes||"-"}</td>
                <td style={{padding:0, background:"#fff8e1"}}>
                  <div style={{display:"flex", gap:0}}>
                    <button data-testid={`mov-marc-dote-${m.Num}`}
                      onClick={() => updateMarcatura(m, { marcatura: m.marcatura === "dote" ? null : "dote" })}
                      title="Marca come DOTE"
                      style={{
                        flex: 1, padding: "5px 4px", fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
                        cursor: "pointer", border: "1px solid #c5cae9",
                        background: m.marcatura === "dote" ? "#1976d2" : "white",
                        color: m.marcatura === "dote" ? "white" : "#37474f",
                      }}>DOTE</button>
                    <button data-testid={`mov-marc-escluso-${m.Num}`}
                      onClick={() => updateMarcatura(m, { marcatura: m.marcatura === "escluso" ? null : "escluso" })}
                      title="Marca come ESCLUSO"
                      style={{
                        flex: 1, padding: "5px 4px", fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
                        cursor: "pointer", border: "1px solid #c5cae9", borderLeft: "none",
                        background: m.marcatura === "escluso" ? "#c62828" : "white",
                        color: m.marcatura === "escluso" ? "white" : "#37474f",
                      }}>ESCLUSO</button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {total > 0 && (
          <div className="sw-pagination">
            <span>{total.toLocaleString("it-IT")} movimenti</span>
          </div>
        )}
      </div>
    </div>
  );
}


// ============== MODIFICA DATE IN BLOCCO ==============
function BulkDateModal({ movimenti, onClose, onDone }) {
  const [scad, setScad] = useState("");
  const [pag, setPag] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const n = movimenti.length;

  const applica = async () => {
    if (!scad && !pag) { toast.error("Imposta almeno una data (scadenza o pagamento)"); return; }
    const scadDmy = isoToDmy(scad);
    const pagDmy = isoToDmy(pag);
    const msg = `Applicare ${scadDmy ? `scadenza ${scadDmy}` : ""}${scadDmy && pagDmy ? " e " : ""}${pagDmy ? `pagamento ${pagDmy}` : ""} a ${n} movimenti su Segremat?\n\nL'operazione scrive direttamente sul gestionale Segremat.`;
    if (!window.confirm(msg)) return;
    setRunning(true); setResult(null);
    try {
      const items = movimenti.map(m => ({ anno: m.anno, codice_alunno: m.Alu, codice_movimento: m.Num }));
      const body = { items };
      if (scadDmy) body.data_scadenza = scadDmy;
      if (pagDmy) body.data_pagamento = pagDmy;
      const r = await api.post("/riconciliazione/modifica-date-bulk", body);
      setResult(r.data);
      if (r.data.status) toast.success(`${r.data.ok || r.data.count} movimenti aggiornati su Segremat`);
      else toast.error(`Completato con ${(r.data.errors || []).length} errori`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore operazione massiva");
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="mov-bulk-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 520, borderRadius: 8, overflow: "hidden" }}>
        <div style={{ background: "#00695c", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>🗓 Modifica date in blocco</strong>
          <button onClick={onClose} data-testid="mov-bulk-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 13, color: "#37474f", marginBottom: 14, background: "#e0f2f1", border: "1px solid #80cbc4", borderRadius: 6, padding: "8px 12px" }}>
            L'operazione verrà applicata ai <b>{n.toLocaleString("it-IT")} movimenti attualmente in lista</b> (filtri correnti). Imposta la/le nuova/e data/e: il campo lasciato vuoto non viene modificato.
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64" }}>Nuova data scadenza</label><br />
              <input type="date" value={scad} onChange={e => setScad(e.target.value)} data-testid="mov-bulk-scadenza"
                style={{ marginTop: 5, padding: "8px 10px", border: "1px solid #80cbc4", borderRadius: 4, fontSize: 14 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64" }}>Nuova data pagamento</label><br />
              <input type="date" value={pag} onChange={e => setPag(e.target.value)} data-testid="mov-bulk-pagamento"
                style={{ marginTop: 5, padding: "8px 10px", border: "1px solid #80cbc4", borderRadius: 4, fontSize: 14 }} />
            </div>
          </div>
          {result && (
            <div data-testid="mov-bulk-result" style={{ marginTop: 14, fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: result.status ? "#2e7d32" : "#c62828" }}>
                {result.status ? "✓" : "⚠"} Aggiornati {result.ok ?? result.count} / {result.count} movimenti
              </div>
              {(result.errors || []).length > 0 && (
                <div style={{ marginTop: 6, maxHeight: 140, overflowY: "auto", background: "#fff3e0", border: "1px solid #ffcc80", borderRadius: 4, padding: 8, fontSize: 12 }}>
                  {(result.errors).slice(0, 50).map((er, i) => <div key={i}>• {String(er)}</div>)}
                </div>
              )}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <button onClick={result && result.status ? onDone : onClose} data-testid="mov-bulk-cancel"
              style={{ padding: "8px 14px", background: "#eceff1", color: "#37474f", border: "none", borderRadius: 4, fontWeight: 600, cursor: "pointer" }}>
              {result && result.status ? "Chiudi e ricarica" : "Annulla"}
            </button>
            <button onClick={applica} disabled={running} data-testid="mov-bulk-apply"
              style={{ padding: "8px 18px", background: running ? "#90a4ae" : "#00695c", color: "white", border: "none", borderRadius: 4, fontWeight: 800, cursor: running ? "wait" : "pointer" }}>
              {running ? "⏳ Applico..." : `Applica a ${n} movimenti`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== INVIA SOLLECITI DI PAGAMENTO ==============
function SollecitiModal({ items, descrizione, onClose }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showDest, setShowDest] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await api.post("/solleciti/preview", { items });
        setData(r.data);
        setSubject(r.data.default_subject || "Sollecito di pagamento");
        setBody(r.data.default_body || "");
      } catch (e) {
        setErr(e.response?.data?.detail || "Errore nel calcolo dei destinatari");
      } finally { setLoading(false); }
    })();
  }, [items]);

  const invia = async () => {
    if (!data || data.tot_destinatari === 0) { toast.error("Nessun destinatario con email valida"); return; }
    const msg = `Inviare il sollecito a ${data.tot_destinatari} famiglie (${data.tot_email_indirizzi} indirizzi email)?\n\n` +
      (data.tot_senza_email ? `⚠️ ${data.tot_senza_email} alunni senza email NON riceveranno nulla.\n\n` : "") +
      `L'email verrà inviata realmente.`;
    if (!window.confirm(msg)) return;
    setSending(true); setResult(null);
    try {
      const r = await api.post("/solleciti/invia", { items, subject, body });
      setResult(r.data);
      if (r.data.mocked) toast.message("Invio simulato (email non configurata)");
      else toast.success(`Solleciti inviati: ${r.data.inviati} · errori: ${r.data.errori}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'invio");
    } finally { setSending(false); }
  };

  const allGruppi = data ? [...(data.gruppi || []), ...(data.senza_email || [])] : [];
  const sampleGruppo = data ? ((data.gruppi && data.gruppi[0]) || (data.senza_email && data.senza_email[0]) || null) : null;

  const escHtml = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const buildPreviewHtml = (bodyPlain, g) => {
    if (!g) return "";
    const oggi = new Date().toLocaleDateString("it-IT");
    let html = escHtml(bodyPlain || "")
      .replace(/\{nome_completo\}/g, escHtml(g.nome_completo))
      .replace(/\{totale\}/g, escHtml(g.totale_fmt || formatCurrency(g.totale)))
      .replace(/\{data_oggi\}/g, oggi)
      .replace(/\n/g, "<br/>");
    html = html.replace(/\{elenco_movimenti\}/g, g.elenco_html || "");
    return html;
  };
  const previewHtml = buildPreviewHtml(body, sampleGruppo);

  return (
    <div data-testid="mov-solleciti-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 820, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto", borderRadius: 8 }}>
        <div style={{ background: "#6a1b9a", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0 }}>
          <strong>📧 Invia solleciti di pagamento</strong>
          <button onClick={onClose} data-testid="mov-solleciti-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          {loading && <div style={{ padding: 20, textAlign: "center", color: "#666" }}>Calcolo destinatari…</div>}
          {!loading && err && <div data-testid="mov-solleciti-error" style={{ padding: 12, background: "#ffebee", border: "1px solid #ef9a9a", borderRadius: 6, color: "#c62828" }}>{err}</div>}
          {!loading && data && !result && (
            <>
              <div style={{ fontSize: 13, color: "#37474f", background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 6, padding: "10px 12px", marginBottom: 14 }}>
                {descrizione
                  ? <>Descrizione filtrata: <b>{descrizione}</b></>
                  : <>⚠️ Nessun filtro descrizione: verranno inclusi <b>tutti</b> i movimenti non pagati in lista.</>}
                <div style={{ marginTop: 6 }} data-testid="mov-solleciti-summary">
                  <b>{data.tot_destinatari}</b> famiglie con email · <b>{data.tot_email_indirizzi}</b> indirizzi · <b>{data.tot_movimenti}</b> movimenti
                  {data.tot_senza_email > 0 && <span style={{ color: "#c62828" }}> · <b>{data.tot_senza_email}</b> senza email</span>}
                </div>
                {!data.resend_configured && <div style={{ marginTop: 6, color: "#ef6c00", fontWeight: 700 }}>Attenzione: invio email non configurato — l'invio sarà simulato.</div>}
              </div>

              <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64" }}>Oggetto</label>
              <input data-testid="mov-solleciti-subject" value={subject} onChange={e => setSubject(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", marginTop: 4, marginBottom: 12, padding: "8px 10px", border: "1px solid #ce93d8", borderRadius: 4, fontSize: 14 }} />

              <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64" }}>Testo dell'email (modificabile)</label>
              <textarea data-testid="mov-solleciti-body" value={body} onChange={e => setBody(e.target.value)} rows={9}
                style={{ width: "100%", boxSizing: "border-box", marginTop: 4, padding: "8px 10px", border: "1px solid #ce93d8", borderRadius: 4, fontSize: 13, fontFamily: "inherit", lineHeight: 1.5 }} />
              <div style={{ fontSize: 11, color: "#777", marginTop: 4 }}>
                Segnaposto disponibili: <code>{"{nome_completo}"}</code> <code>{"{elenco_movimenti}"}</code> <code>{"{totale}"}</code> <code>{"{data_oggi}"}</code>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#455a64", marginBottom: 6 }}>
                  👁 Anteprima email {sampleGruppo ? <span style={{ fontWeight: 400, color: "#888" }}>(esempio: {sampleGruppo.nome_completo})</span> : null}
                </div>
                <div data-testid="mov-solleciti-preview"
                  style={{ border: "1px solid #e0e0e0", borderRadius: 6, background: "#fafafa", padding: "6px 10px" }}>
                  <div style={{ fontSize: 12, color: "#555", borderBottom: "1px solid #eee", paddingBottom: 6, marginBottom: 10 }}>
                    <b>Oggetto:</b> {subject || <span style={{ color: "#aaa" }}>(nessun oggetto)</span>}
                  </div>
                  <div style={{ background: "white", border: "1px solid #eee", borderRadius: 4, padding: "12px 14px" }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <button data-testid="mov-solleciti-toggle-dest" onClick={() => setShowDest(v => !v)}
                  style={{ background: "none", border: "none", color: "#6a1b9a", cursor: "pointer", fontWeight: 700, fontSize: 12, padding: 0 }}>
                  {showDest ? "▾ Nascondi destinatari" : `▸ Mostra destinatari (${allGruppi.length})`}
                </button>
                {showDest && (
                  <div style={{ marginTop: 8, maxHeight: 220, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 6 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead><tr style={{ background: "#f5f5f5" }}>
                        <th style={{ textAlign: "left", padding: "6px 10px" }}>Alunno</th>
                        <th style={{ textAlign: "left", padding: "6px 10px" }}>Email</th>
                        <th style={{ textAlign: "right", padding: "6px 10px" }}>Totale</th>
                      </tr></thead>
                      <tbody>
                        {allGruppi.map(g => (
                          <tr key={g.alu} style={{ borderTop: "1px solid #eee" }}>
                            <td style={{ padding: "6px 10px" }}>{g.nome_completo}</td>
                            <td style={{ padding: "6px 10px", color: g.emails.length ? "#2e7d32" : "#c62828" }}>
                              {g.emails.length ? g.emails.join(", ") : "— nessuna email —"}
                            </td>
                            <td style={{ padding: "6px 10px", textAlign: "right" }}>{formatCurrency(g.totale)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
                <button onClick={onClose} data-testid="mov-solleciti-cancel"
                  style={{ padding: "8px 14px", background: "#eceff1", color: "#37474f", border: "none", borderRadius: 4, fontWeight: 600, cursor: "pointer" }}>Annulla</button>
                <button onClick={invia} disabled={sending || data.tot_destinatari === 0} data-testid="mov-solleciti-send"
                  style={{ padding: "8px 18px", background: (sending || data.tot_destinatari === 0) ? "#b0bec5" : "#6a1b9a", color: "white", border: "none", borderRadius: 4, fontWeight: 800, cursor: (sending || data.tot_destinatari === 0) ? "not-allowed" : "pointer" }}>
                  {sending ? "Invio in corso…" : `Invia a ${data.tot_destinatari} famiglie`}
                </button>
              </div>
            </>
          )}

          {result && (
            <div data-testid="mov-solleciti-result" style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 800, color: result.errori ? "#ef6c00" : "#2e7d32", fontSize: 16, marginBottom: 8 }}>
                {result.mocked ? "Invio simulato completato" : "Solleciti inviati"}
              </div>
              <div>✓ Inviati: <b>{result.inviati}</b></div>
              {result.errori > 0 && <div style={{ color: "#c62828" }}>⚠ Errori: <b>{result.errori}</b></div>}
              {result.senza_email > 0 && <div style={{ color: "#c62828" }}>Senza email (non inviati): <b>{result.senza_email}</b></div>}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                <button onClick={onClose} data-testid="mov-solleciti-done"
                  style={{ padding: "8px 18px", background: "#6a1b9a", color: "white", border: "none", borderRadius: 4, fontWeight: 800, cursor: "pointer" }}>Chiudi</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============== ESTRATTO CONTO MODAL ==============
function EstrattoContoModal({ codiceAlunno, anno, alunnoInfo, onClose }) {
  const [allegati, setAllegati] = useState([]);
  const [selectedAllegato, setSelectedAllegato] = useState("");
  const [movimenti, setMovimenti] = useState([]);
  const [loadingMovs, setLoadingMovs] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);
  const [tuttiAnni, setTuttiAnni] = useState(true);

  // Carica allegati DOCX disponibili
  useEffect(() => {
    api.get("/comunicator/allegati-docx").then(r => {
      setAllegati(r.data?.items || []);
      if ((r.data?.items || []).length > 0) setSelectedAllegato(r.data.items[0].id);
    }).catch(() => { setAllegati([]); });
  }, []);

  // Carica movimenti dell'alunno (tutti gli anni o solo l'anno corrente)
  useEffect(() => {
    if (!codiceAlunno) return;
    setLoadingMovs(true); setErr(null);
    const params = { sort_by: "DtaSca", sort_dir: "asc", solo_addebiti: true };
    if (!tuttiAnni && anno) params.anno = anno;
    api.get(`/comunicator/movimenti-alunno/${codiceAlunno}`, { params })
      .then(r => setMovimenti(r.data?.items || []))
      .catch(e => setErr(e.response?.data?.detail || "Errore caricamento movimenti"))
      .finally(() => setLoadingMovs(false));
  }, [codiceAlunno, anno, tuttiAnni]);

  const totale = movimenti.reduce((s, m) => s + (m.importo || 0), 0);
  const totPagato = movimenti.filter(m => m.pagato).reduce((s, m) => s + (m.importo || 0), 0);

  const genera = async () => {
    if (!selectedAllegato) { setErr("Seleziona un modulo DOCX"); return; }
    setGenerating(true); setErr(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    try {
      const r = await api.post(`/comunicator/allegati-docx/${selectedAllegato}/anteprima-pdf`,
        { cod: codiceAlunno, anno, movimenti },
        { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) {
      let detail = "Errore generazione PDF";
      try {
        const txt = await e.response?.data?.text?.();
        detail = JSON.parse(txt)?.detail || txt || detail;
      } catch (e2) { detail = e.message || detail; }
      setErr(detail);
    } finally { setGenerating(false); }
  };

  const scaricaPdf = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `EstrattoConto_${(alunnoInfo?.Cog || "alunno").replace(/\s/g, "_")}_${codiceAlunno}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div data-testid="estratto-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={() => { if (previewUrl) URL.revokeObjectURL(previewUrl); onClose(); }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 1100, height: "92vh", borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#7b1fa2", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>🖨️ Stampa Estratto Conto — {alunnoInfo?.Cog} {alunnoInfo?.Nom} (cod. {codiceAlunno})</strong>
          <button onClick={() => { if (previewUrl) URL.revokeObjectURL(previewUrl); onClose(); }}
            style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ padding: 16, borderBottom: "1px solid #ddd", background: "#fafafa" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>📎 Modulo allegato:</label>
            <select value={selectedAllegato} onChange={e => setSelectedAllegato(e.target.value)}
              data-testid="estratto-allegato-select"
              style={{ padding: "6px 10px", border: "1px solid #7b1fa2", borderRadius: 3, fontSize: 13, minWidth: 350 }}>
              <option value="">— Nessun modulo selezionato —</option>
              {allegati.map(a => (
                <option key={a.id} value={a.id}>{a.nome} · {a.filename}</option>
              ))}
            </select>
            {allegati.length === 0 && (
              <span style={{ fontSize: 11, color: "#c62828", fontStyle: "italic" }}>
                Nessun allegato DOCX caricato. Vai in <b>Comunicazioni → 📎 Allegati DOCX</b>.
              </span>
            )}
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "#666", marginLeft: "auto" }}>
              <input type="checkbox" checked={tuttiAnni} onChange={e => setTuttiAnni(e.target.checked)}
                data-testid="estratto-tutti-anni" />
              📅 Tutti gli anni {tuttiAnni ? "" : `(solo ${anno})`}
            </label>
          </div>
          <div style={{ fontSize: 11, color: "#666" }}>
            <b>{loadingMovs ? "caricamento..." : `${movimenti.length} movimenti`}</b>
            {!loadingMovs && (
              <> · Totale: <b>€ {totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b> · Pagato: <b>€ {totPagato.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b> · Da pagare: <b style={{ color: "#c62828" }}>€ {(totale - totPagato).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b></>
            )}
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
            <button onClick={genera} disabled={generating || !selectedAllegato || loadingMovs}
              data-testid="estratto-genera"
              style={{ padding: "8px 18px", background: generating ? "#bbb" : "#7b1fa2", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: generating ? "wait" : "pointer", borderRadius: 3 }}>
              {generating ? "⏳ Generazione..." : "🔍 Genera Anteprima PDF"}
            </button>
            {previewUrl && (
              <button onClick={scaricaPdf} data-testid="estratto-download"
                style={{ padding: "8px 18px", background: "#1565c0", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
                📥 Scarica PDF
              </button>
            )}
            {previewUrl && (
              <button onClick={() => { window.open(previewUrl, "_blank"); }} data-testid="estratto-print"
                style={{ padding: "8px 18px", background: "#43a047", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
                🖨️ Stampa
              </button>
            )}
          </div>
          {err && (
            <div style={{ marginTop: 8, padding: 8, background: "#ffebee", color: "#c62828", fontSize: 12, borderRadius: 3, border: "1px solid #c62828" }}>{err}</div>
          )}
        </div>

        <div style={{ flex: 1, overflow: "hidden", background: "#525659" }}>
          {previewUrl ? (
            <iframe title="Anteprima PDF" src={previewUrl} style={{ width: "100%", height: "100%", border: "none" }} />
          ) : (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 14 }}>
              {loadingMovs
                ? "Caricamento movimenti..."
                : selectedAllegato
                  ? "Clicca \"🔍 Genera Anteprima PDF\" per visualizzare l'estratto conto"
                  : "Seleziona un modulo allegato dall'elenco sopra"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
